import { useSuspenseQuery } from "@apollo/client";
import {
  Button,
  CurrencyAmountUI,
  cn,
  DataTable,
  DateUI,
  Icon,
  LabelPill,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
  toast,
} from "@repo/ui";
import { formatCurrencyAmount, NotFoundError, neverFunc } from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { RiBuilding2Line } from "react-icons/ri";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { StripePayoutQueueItemRowFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const tabs = ["pending", "received", "paid", "voided"] as const;

type Tab = (typeof tabs)[number];

const columns: ColumnDef<StripePayoutQueueItemRowFragment>[] = [
  {
    id: "date",
    header: "Arrival date",
    accessorKey: "arrivalDate",
    cell(c) {
      return <DateUI date={new Date(c.row.original.arrivalDate)} />;
    },
  },
  {
    id: "organisation",
    header: "Organisation",
    accessorFn(c) {
      return c.organisation?.name;
    },
    cell(c) {
      return (
        <div className="flex max-w-32 flex-row items-center gap-2 overflow-hidden">
          <Icon
            imageUrl={c.row.original.organisation?.squareLogoUrl ?? undefined}
            fallbackIcon={<RiBuilding2Line />}
            size="6"
          />
          <p className="truncate">{c.row.original.organisation?.name}</p>
        </div>
      );
    },
  },
  {
    id: "grossAmount",
    header: "Gross amount",
    accessorFn(c) {
      return formatCurrencyAmount({ ...c.grossAmount, notation: "standard" });
    },
    cell(c) {
      return <CurrencyAmountUI ca={c.row.original.grossAmount} nonCopyable />;
    },
  },
  {
    id: "feeAmount",
    header: "Fee amount",
    accessorFn(c) {
      return formatCurrencyAmount({ ...c.feeAmount, notation: "standard" });
    },
    cell(c) {
      return <CurrencyAmountUI ca={c.row.original.feeAmount} nonCopyable />;
    },
  },
  {
    id: "netAmount",
    header: () => <span className="font-semibold">Net amount</span>,
    accessorFn(c) {
      return formatCurrencyAmount({ ...c.netAmount, notation: "standard" });
    },
    cell(c) {
      return (
        <div className="flex flex-row items-center gap-3">
          <strong>
            <CurrencyAmountUI ca={c.row.original.netAmount} nonCopyable />
          </strong>
          <div className="invisible group-hover/data-table-row:visible">
            <Button
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const amountToCopy = new Intl.NumberFormat("en-AU", {
                  useGrouping: false, // Removes thousands separators
                  minimumFractionDigits: 2, // Keeps decimal precision
                  maximumFractionDigits: 2,
                }).format(c.row.original.netAmount.amount.toNumber());
                navigator.clipboard.writeText(amountToCopy);
                toast({
                  kind: "success",
                  title: `${amountToCopy} copied to clipboard`,
                  autoHide: true,
                });
              }}
            >
              Copy
            </Button>
          </div>
        </div>
      );
    },
  },
  {
    id: "linkedMovement",
    header: "Linked transfer/payout",
    accessorFn(c) {
      return c.transfer?.shortRef ?? c.payout?.shortRef;
    },
    cell(c) {
      return <LinkedMovementLink item={c.row.original} />;
    },
  },
  {
    id: "stripePayoutStatus",
    header: "Stripe Payout Status",
    accessorFn(c) {
      return c.stripePayoutStatus;
    },
    cell(c) {
      return (
        <LabelPill
          variant={
            c.row.original.stripePayoutStatus === "PAID" ? "success" : "warning"
          }
          label={c.row.original.stripePayoutStatus ?? "UNKNOWN"}
        />
      );
    },
  },
];

export const Route = createFileRoute("/__auth/stripe-payout-queue/$tab")({
  head: () => opsHead("Stripe Payout Queue"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const params = useParams({ strict: false });

  let selectedTab: Tab;
  if (tabs.includes(params["tab"] as Tab)) {
    selectedTab = params["tab"] as Tab;
  } else {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
    query StripePayoutQueuePage {
        stripePayoutQueueItems {
            id
            ...StripePayoutQueueItemRow
        }
    }

    fragment StripePayoutQueueItemRow on StripePayoutQueueItem {
        id
        organisation {
            id
            name
            squareLogoUrl
        }
        transfer {
            id
            shortRef
        }
        payout {
            id
            shortRef
        }
        grossAmount {
            currency
            amount
        }
        feeAmount {
            currency
            amount
        }
        netAmount {
            currency
            amount
        }
        status
        stripePayoutStatus
        arrivalDate
        stripeId
        createdAt
    }
  `),
  );

  const rows = (data.stripePayoutQueueItems ?? []).filter((r) => {
    switch (selectedTab) {
      case "paid":
        return r.status === "PAID";
      case "pending":
        return r.status === "PENDING";
      case "received":
        return r.status === "RECEIVED";
      case "voided":
        return r.status === "VOIDED";
      default:
        throw neverFunc(selectedTab);
    }
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sidePaneVisible, setSidePaneVisible] = useState(false);
  const selectedItem = (data.stripePayoutQueueItems ?? []).find(
    (item) => item.id === selectedId,
  );

  const tabToBadgeNumber: Record<Tab, number> = {
    paid: (data.stripePayoutQueueItems ?? []).reduce<number>(
      (acc, curr) => (curr.status === "PAID" ? acc + 1 : acc),
      0,
    ),
    pending: (data.stripePayoutQueueItems ?? []).reduce<number>(
      (acc, curr) => (curr.status === "PENDING" ? acc + 1 : acc),
      0,
    ),
    received: (data.stripePayoutQueueItems ?? []).reduce<number>(
      (acc, curr) => (curr.status === "RECEIVED" ? acc + 1 : acc),
      0,
    ),
    voided: (data.stripePayoutQueueItems ?? []).reduce<number>(
      (acc, curr) => (curr.status === "VOIDED" ? acc + 1 : acc),
      0,
    ),
  };

  return (
    <AdminPageContainer testId="stripe-payout-queue">
      <AdminPageHeader title="Stripe Payout Queue" />
      <div className="flex items-start gap-2">
        <div className="grid w-full flex-1 gap-6" style={{ zIndex: 1 }}>
          <div className="flex justify-start gap-4">
            <OpsTab
              label="Pending"
              badgeNumber={tabToBadgeNumber["pending"]}
              to="pending"
            />
            <OpsTab
              label="Received"
              badgeNumber={tabToBadgeNumber["received"]}
              to="received"
            />
            <OpsTab
              label="Paid"
              badgeNumber={tabToBadgeNumber["paid"]}
              to="paid"
            />
            <OpsTab
              label="Voided"
              badgeNumber={tabToBadgeNumber["voided"]}
              to="voided"
            />
          </div>

          <div className="flex w-full flex-col gap-6 rounded-xl border bg-white px-6 pt-6 pb-6 shadow-card">
            <div className="flex justify-between">
              <Txt variant="card-title">Stripe payouts</Txt>
            </div>

            <DataTable
              key={selectedTab}
              data={rows}
              activeRowId={
                sidePaneVisible ? (selectedId ?? undefined) : undefined
              }
              hidePagination={true}
              pageSize={10_000}
              onRowClick={(row) => {
                setSelectedId(row.original.id);
                setSidePaneVisible(
                  sidePaneVisible ? row.original.id !== selectedId : true,
                );
              }}
              cellClassName="py-1 px-4"
              rowClassName="group/data-table-row"
              columns={columns}
            />
          </div>
        </div>

        <div
          className={cn(
            "absolute top-0 right-0 h-full w-1/2 transform overflow-auto border-l bg-white p-8 shadow-popover duration-300 ease-in-out",
            sidePaneVisible
              ? "translate-x-0"
              : "pointer-events-none translate-x-full",
          )}
          style={{
            zIndex: 1,
          }}
        >
          {selectedItem ? (
            <div className="flex h-full flex-col gap-16">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-tertiary text-xs uppercase tracking-wide">
                    Stripe payout
                  </div>
                  <div className="mt-2 font-mono text-lg">
                    {selectedItem.stripeId}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <StatusUI status={selectedItem.status} />
                    <span className="text-tertiary text-xs">
                      {selectedItem.id}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<X />}
                  aria-label="Close"
                  onClick={() => {
                    setSidePaneVisible(false);
                  }}
                />
              </div>

              <div>
                <Txt variant="section-title">Payout summary</Txt>
                <div className="grid grid-cols-[264px_70%] gap-8">
                  <div>
                    <DetailRow
                      label="Gross amount"
                      value={<CurrencyAmountUI ca={selectedItem.grossAmount} />}
                    />
                    <DetailRow
                      label="Fee amount"
                      value={<CurrencyAmountUI ca={selectedItem.feeAmount} />}
                    />
                    <DetailRow
                      label="Net amount"
                      value={<CurrencyAmountUI ca={selectedItem.netAmount} />}
                    />
                  </div>
                  <div>
                    <DetailRow
                      label="Organisation"
                      value={
                        selectedItem.organisation ? (
                          <Link
                            to={`/organisations/${selectedItem.organisation.id}`}
                            className="hover:underline"
                          >
                            <div className="flex max-w-32 flex-row items-center gap-2 overflow-hidden">
                              <Icon
                                imageUrl={
                                  selectedItem.organisation?.squareLogoUrl ??
                                  undefined
                                }
                                fallbackIcon={<RiBuilding2Line />}
                                size="5"
                              />
                              <p className="truncate">
                                {selectedItem.organisation?.name}
                              </p>
                            </div>
                          </Link>
                        ) : (
                          "-"
                        )
                      }
                    />
                    <DetailRow
                      label="Arrival date"
                      value={
                        <DateUI date={new Date(selectedItem.arrivalDate)} />
                      }
                    />
                    <DetailRow
                      label="Stripe ID"
                      value={
                        <div className="overflow-hidden">
                          <p className="truncate font-mono text-sm">
                            {selectedItem.stripeId}
                          </p>
                        </div>
                      }
                    />
                    <DetailRow
                      label="Linked transfer/payout"
                      value={<LinkedMovementLink item={selectedItem} />}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-tertiary">
              Select a payout to see details.
            </div>
          )}
        </div>
        {sidePaneVisible && (
          <div
            className="absolute top-0 left-0 h-full w-full overflow-hidden"
            style={{
              zIndex: 0,
            }}
            onClick={() => {
              setSidePaneVisible(false);
            }}
          ></div>
        )}
      </div>
    </AdminPageContainer>
  );
}

function LinkedMovementLink({
  item,
}: {
  item: StripePayoutQueueItemRowFragment;
}) {
  if (item.transfer) {
    return (
      <Link
        to={`/transfers/${item.transfer.id}`}
        className="font-mono text-sm hover:underline"
      >
        {item.transfer.shortRef}
      </Link>
    );
  }

  if (item.payout) {
    return (
      <Link
        to={`/payouts/${item.payout.id}`}
        className="font-mono text-sm hover:underline"
      >
        {item.payout.shortRef}
      </Link>
    );
  }

  return <span>-</span>;
}

function OpsTab({
  to,
  label,
  badgeNumber,
}: {
  to: Tab;
  label: React.ReactNode;
  badgeNumber?: number | null;
}) {
  return (
    <Link
      to={`/stripe-payout-queue/${to}`}
      activeProps={{ className: "active" }}
      className={cn(
        "group grid grid-flow-row place-items-start gap-2 whitespace-nowrap rounded-lg p-4 ring-offset-white ",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "min-w-40 max-w-60",
        "disabled:pointer-events-none disabled:opacity-50",
        "font-normal text-secondary",
        "border border-transparent",
        "[&.active]:bg-secondary [&.active]:font-medium [&.active]:text-primary [&.active]:shadow-popover",
        "hover:bg-neutral-muted hover:shadow-card",
        "cursor-pointer",
      )}
    >
      <span className="text-sm text-tertiary group-[&.active]:text-inherit">
        {label}
      </span>
      {badgeNumber !== undefined && (
        <span className="text-lg">{badgeNumber}</span>
      )}
    </Link>
  );
}
